/**
 * createOutputNode - Utility for auto-creating BlockNodes connected to source nodes
 *
 * Used by KnowledgeNode and ChatNode to create output BlockNodes
 * when streaming content (answers, pinned messages).
 *
 * The created BlockNode is positioned to the right of the source node
 * and connected via an edge from source's output to block's input.
 */

import { nanoid } from 'nanoid';
import type { Node, Edge } from '@xyflow/react';
import * as Y from 'yjs';
import { createDefaultBlockData, type KnowledgeCitation } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

export interface CreateOutputNodeParams {
  /** ID of the source node (KnowledgeNode or ChatNode) */
  sourceNodeId: string;

  /** Position of the source node */
  sourceNodePosition: { x: number; y: number };

  /** Optional title for the new BlockNode */
  title?: string;

  /** Optional initial content for the BlockNode */
  content?: string;

  /** Optional citations to include in the BlockNode */
  citations?: KnowledgeCitation[];

  /** Yjs map containing all nodes */
  nodesMap: Y.Map<Node>;

  /** Yjs map containing all edges */
  edgesMap: Y.Map<Edge>;

  /** Wrapper function for undo/redo support */
  makeChange: (fn: () => void) => void;
}

export interface CreateOutputNodeResult {
  /** ID of the newly created BlockNode */
  nodeId: string;

  /** ID of the edge connecting source to new node */
  edgeId: string;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Creates a new BlockNode connected to a source node.
 *
 * @param params - Configuration for creating the output node
 * @returns Object containing the new node ID and edge ID
 *
 * @example
 * ```ts
 * const result = createOutputNode({
 *   sourceNodeId: 'knowledge-1',
 *   sourceNodePosition: { x: 100, y: 100 },
 *   title: 'Answer',
 *   nodesMap,
 *   edgesMap,
 *   makeChange,
 * });
 * // result.nodeId is the new BlockNode ID
 * ```
 */
export function createOutputNode(params: CreateOutputNodeParams): CreateOutputNodeResult {
  const {
    sourceNodeId,
    sourceNodePosition,
    title = '',
    content = '<p></p>',
    citations,
    nodesMap,
    edgesMap,
    makeChange,
  } = params;

  // Generate unique IDs
  const nodeId = nanoid();
  const edgeId = nanoid();

  // Calculate position: offset 350px to the right of source
  const position = {
    x: sourceNodePosition.x + 350,
    y: sourceNodePosition.y,
  };

  // Create the BlockNode data
  const blockData = createDefaultBlockData();
  blockData.title = title;
  blockData.content = content;
  if (citations) {
    blockData.citations = citations;
  }

  // Create the new node
  const newNode: Node = {
    id: nodeId,
    type: 'block',
    position,
    data: blockData,
  };

  // Create the edge from source output to new node input
  const newEdge: Edge = {
    id: edgeId,
    source: sourceNodeId,
    sourceHandle: 'output',
    target: nodeId,
    targetHandle: 'input',
  };

  // Wrap all changes in makeChange for undo/redo support
  makeChange(() => {
    nodesMap.set(nodeId, newNode);
    edgesMap.set(edgeId, newEdge);
  });

  return { nodeId, edgeId };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find an existing BlockNode connected to the source's output port.
 *
 * @param sourceNodeId - ID of the source node
 * @param edgesMap - Yjs map of all edges
 * @param nodesMap - Yjs map of all nodes
 * @returns The connected BlockNode ID if found, null otherwise
 */
export function findConnectedBlockNode(
  sourceNodeId: string,
  edgesMap: Y.Map<Edge>,
  nodesMap: Y.Map<Node>
): string | null {
  // Find edges where source is our node
  const edges = Array.from(edgesMap.values());

  for (const edge of edges) {
    if (edge.source === sourceNodeId && edge.sourceHandle === 'output') {
      // Check if target is a BlockNode
      const targetNode = nodesMap.get(edge.target);
      if (targetNode && targetNode.type === 'block') {
        return edge.target;
      }
    }
  }

  return null;
}

/**
 * Update content and citations of an existing BlockNode.
 *
 * IMPORTANT: This writes directly to the Tiptap editor's Yjs XmlFragment,
 * not to data.content. This ensures the content appears in the editor.
 *
 * @param nodeId - ID of the BlockNode to update
 * @param content - New HTML content
 * @param citations - Optional citations array
 * @param nodesMap - Yjs map of all nodes
 * @param makeChange - Wrapper function for undo/redo
 * @param ydoc - Yjs document for accessing XmlFragments
 */
export function updateBlockNodeContent(
  nodeId: string,
  content: string,
  citations: KnowledgeCitation[] | undefined,
  nodesMap: Y.Map<Node>,
  makeChange: (fn: () => void) => void,
  ydoc?: Y.Doc
): void {
  makeChange(() => {
    const existing = nodesMap.get(nodeId);
    if (existing && existing.type === 'block') {
      const existingData = existing.data as Record<string, unknown>;

      // If ydoc is provided, write to the XmlFragment
      if (ydoc) {
        // Get the Tiptap XmlFragment for this block
        const fragment = ydoc.getXmlFragment(`richtext-${nodeId}`);

        // REPLACE the entire content (clear first)
        fragment.delete(0, fragment.length);

        // Parse the HTML content and insert into the XmlFragment
        // Create a simple paragraph structure from the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        // Create Yjs XML elements from the HTML
        const paragraphs = tempDiv.querySelectorAll('p');
        paragraphs.forEach((p) => {
          const xmlParagraph = new Y.XmlElement('paragraph');
          const textContent = p.textContent || '';
          if (textContent) {
            const xmlText = new Y.XmlText(textContent);
            xmlParagraph.insert(0, [xmlText]);
          }
          fragment.push([xmlParagraph]);
        });
      }

      // Update citations in node data
      if (citations !== undefined) {
        const updatedData: Record<string, unknown> = { ...existingData, citations };
        nodesMap.set(nodeId, {
          ...existing,
          data: updatedData,
        });
      }
    }
  });
}

/**
 * Clear a BlockNode's content (prepare for new streaming).
 *
 * IMPORTANT: This clears the Tiptap editor's Yjs XmlFragment,
 * not just data.content.
 *
 * @param nodeId - ID of the BlockNode to clear
 * @param nodesMap - Yjs map of all nodes
 * @param makeChange - Wrapper function for undo/redo
 * @param ydoc - Yjs document for accessing XmlFragments
 */
export function clearBlockNode(
  nodeId: string,
  nodesMap: Y.Map<Node>,
  makeChange: (fn: () => void) => void,
  ydoc?: Y.Doc
): void {
  makeChange(() => {
    const existing = nodesMap.get(nodeId);
    if (existing && existing.type === 'block') {
      const existingData = existing.data as Record<string, unknown>;

      // If ydoc is provided, clear the XmlFragment
      if (ydoc) {
        // Get the Tiptap XmlFragment for this block
        const fragment = ydoc.getXmlFragment(`richtext-${nodeId}`);

        // Clear all content from the fragment
        fragment.delete(0, fragment.length);

        // Insert an empty paragraph
        const xmlParagraph = new Y.XmlElement('paragraph');
        fragment.push([xmlParagraph]);
      }

      // Update node data to clear citations
      nodesMap.set(nodeId, {
        ...existing,
        data: {
          ...existingData,
          citations: undefined,
        },
      });
    }
  });
}
